// AI Negotiation Assistant Tab
// VSTL Trade Manager - Phase 4

import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { MessageSquare, Copy, Send, RefreshCw, Mail, Phone } from 'lucide-react';

const NegotiationTab = () => {
  const [mode, setMode] = useState('counter-offer');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [result, setResult] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Counter-offer form
  const [coSkuId, setCoSkuId] = useState('');
  const [coSupplier, setCoSupplier] = useState('');
  const [coAskPrice, setCoAskPrice] = useState('');
  const [coQuantity, setCoQuantity] = useState('1');
  const [coContext, setCoContext] = useState('');

  // Reply draft form
  const [rdSupplier, setRdSupplier] = useState('');
  const [rdMessage, setRdMessage] = useState('');
  const [rdSkuId, setRdSkuId] = useState('');
  const [rdContextType, setRdContextType] = useState('general');

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/ai-engine/negotiation/history?limit=20');
      setHistory(res.data.data || []);
    } catch (e) {
      console.error('Failed to load negotiation history:', e);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const generateCounterOffer = async () => {
    if (!coSkuId || !coSupplier || !coAskPrice) {
      toast.error('SKU, Supplier, and Ask Price are required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/ai-engine/negotiation/counter-offer', {
        sku_id: coSkuId,
        supplier_name: coSupplier,
        ask_price: parseFloat(coAskPrice),
        quantity: parseInt(coQuantity) || 1,
        additional_context: coContext
      });
      setResult(res.data);
      fetchHistory();
      toast.success('Counter-offer generated');
    } catch (e) {
      toast.error('Failed to generate counter-offer');
    } finally {
      setLoading(false);
    }
  };

  const generateReplyDraft = async () => {
    if (!rdSupplier || !rdMessage) {
      toast.error('Supplier name and message are required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/ai-engine/negotiation/reply-draft', {
        supplier_name: rdSupplier,
        incoming_message: rdMessage,
        context_type: rdContextType,
        sku_id: rdSkuId
      });
      setResult(res.data);
      fetchHistory();
      toast.success('Reply draft generated');
    } catch (e) {
      toast.error('Failed to generate reply draft');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="negotiation-tab">
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          variant={mode === 'counter-offer' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setMode('counter-offer'); setResult(null); }}
          data-testid="counter-offer-mode-btn"
        >
          <MessageSquare className="h-4 w-4 mr-1" /> Counter-Offer
        </Button>
        <Button
          variant={mode === 'reply-draft' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setMode('reply-draft'); setResult(null); }}
          data-testid="reply-draft-mode-btn"
        >
          <Mail className="h-4 w-4 mr-1" /> Reply Draft
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Input Form */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {mode === 'counter-offer' ? 'Generate Counter-Offer' : 'Generate Reply Draft'}
            </CardTitle>
            <CardDescription>
              {mode === 'counter-offer'
                ? 'AI will create a negotiation message based on market data'
                : 'AI will draft a professional reply to a supplier message'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mode === 'counter-offer' ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">SKU ID</Label>
                    <Input value={coSkuId} onChange={e => setCoSkuId(e.target.value)} placeholder="e.g. 1.2TB SAS HDD" data-testid="co-sku-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Supplier Name</Label>
                    <Input value={coSupplier} onChange={e => setCoSupplier(e.target.value)} placeholder="Supplier name" data-testid="co-supplier-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Ask Price ($)</Label>
                    <Input type="number" value={coAskPrice} onChange={e => setCoAskPrice(e.target.value)} placeholder="0.00" data-testid="co-price-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Quantity</Label>
                    <Input type="number" value={coQuantity} onChange={e => setCoQuantity(e.target.value)} placeholder="1" data-testid="co-qty-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Additional Context (optional)</Label>
                  <Input value={coContext} onChange={e => setCoContext(e.target.value)} placeholder="e.g. urgent need, bulk deal..." data-testid="co-context-input" />
                </div>
                <Button onClick={generateCounterOffer} disabled={loading} className="w-full" data-testid="generate-counter-offer-btn">
                  {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                  {loading ? 'Generating...' : 'Generate Counter-Offer'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Supplier Name</Label>
                    <Input value={rdSupplier} onChange={e => setRdSupplier(e.target.value)} placeholder="Supplier name" data-testid="rd-supplier-input" />
                  </div>
                  <div>
                    <Label className="text-xs">SKU (optional)</Label>
                    <Input value={rdSkuId} onChange={e => setRdSkuId(e.target.value)} placeholder="e.g. 1.2TB SAS HDD" data-testid="rd-sku-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Incoming Message</Label>
                  <textarea
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
                    value={rdMessage}
                    onChange={e => setRdMessage(e.target.value)}
                    placeholder="Paste the supplier's message here..."
                    data-testid="rd-message-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Context Type</Label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={rdContextType}
                    onChange={e => setRdContextType(e.target.value)}
                    data-testid="rd-context-type"
                  >
                    <option value="general">General</option>
                    <option value="price_negotiation">Price Negotiation</option>
                    <option value="availability_inquiry">Availability Inquiry</option>
                    <option value="order_followup">Order Follow-up</option>
                    <option value="complaint">Complaint/Issue</option>
                  </select>
                </div>
                <Button onClick={generateReplyDraft} disabled={loading} className="w-full" data-testid="generate-reply-draft-btn">
                  {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  {loading ? 'Generating...' : 'Generate Reply Draft'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">AI Output</CardTitle>
          </CardHeader>
          <CardContent>
            {!result ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p>Results will appear here</p>
              </div>
            ) : result.type === 'counter_offer' ? (
              <div className="space-y-3" data-testid="counter-offer-result">
                {result.counter_offer_price && (
                  <div className="flex items-center gap-3">
                    <Badge className="bg-green-600 text-white text-sm px-3 py-1">
                      Counter: ${result.counter_offer_price.toFixed(2)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      vs Ask: ${result.ask_price?.toFixed(2)}
                    </span>
                  </div>
                )}
                {result.negotiation_strategy && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Strategy</p>
                    <p className="text-sm">{result.negotiation_strategy}</p>
                  </div>
                )}
                <div className="space-y-2">
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold flex items-center gap-1"><Phone className="h-3 w-3" /> WhatsApp</p>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.message_whatsapp)} data-testid="copy-whatsapp-btn">
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <p className="text-sm">{result.message_whatsapp}</p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold flex items-center gap-1"><Mail className="h-3 w-3" /> Email</p>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.message_email)} data-testid="copy-email-btn">
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <p className="text-sm whitespace-pre-line">{result.message_email}</p>
                  </div>
                </div>
                {result.key_leverage_points?.length > 0 && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Leverage Points</p>
                    <ul className="text-sm list-disc pl-4 space-y-0.5">
                      {result.key_leverage_points.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3" data-testid="reply-draft-result">
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold flex items-center gap-1"><Phone className="h-3 w-3" /> WhatsApp Reply</p>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.reply_whatsapp)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="text-sm">{result.reply_whatsapp}</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold flex items-center gap-1"><Mail className="h-3 w-3" /> Email Reply</p>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.reply_email_body)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  {result.reply_email_subject && (
                    <p className="text-xs text-muted-foreground mb-1">Subject: {result.reply_email_subject}</p>
                  )}
                  <p className="text-sm whitespace-pre-line">{result.reply_email_body}</p>
                </div>
                {result.suggested_next_action && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Suggested Next Action</p>
                    <p className="text-sm">{result.suggested_next_action}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History */}
      {history.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent Negotiations</CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchHistory}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.slice(0, 10).map((item) => (
                    <TableRow key={item.id} data-testid={`nego-row-${item.id}`}>
                      <TableCell className="text-xs">{new Date(item.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {item.type === 'counter_offer' ? 'Counter-Offer' : 'Reply Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-medium">{item.sku_id || '-'}</TableCell>
                      <TableCell className="text-sm">{item.supplier_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                        {item.type === 'counter_offer'
                          ? `Ask: $${item.ask_price?.toFixed(2)} → Counter: $${item.counter_offer_price?.toFixed(2) || '?'}`
                          : item.incoming_message?.substring(0, 50) + '...'
                        }
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedItem(item)}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Detail Modal */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedItem?.type === 'counter_offer' ? 'Counter-Offer Details' : 'Reply Draft Details'}</DialogTitle>
            <DialogDescription>{selectedItem?.supplier_name} — {selectedItem?.sku_id || 'General'}</DialogDescription>
          </DialogHeader>
          {selectedItem && selectedItem.type === 'counter_offer' && (
            <div className="space-y-3">
              {selectedItem.counter_offer_price && (
                <div className="flex items-center gap-3">
                  <Badge className="bg-green-600 text-white">Counter: ${selectedItem.counter_offer_price.toFixed(2)}</Badge>
                  <span className="text-sm text-muted-foreground">Ask: ${selectedItem.ask_price?.toFixed(2)}</span>
                </div>
              )}
              <div className="p-3 border rounded-lg">
                <p className="text-xs font-semibold mb-1">WhatsApp Message</p>
                <p className="text-sm">{selectedItem.message_whatsapp}</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs font-semibold mb-1">Email Message</p>
                <p className="text-sm whitespace-pre-line">{selectedItem.message_email}</p>
              </div>
              {selectedItem.negotiation_strategy && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs font-semibold mb-1">Strategy</p>
                  <p className="text-sm">{selectedItem.negotiation_strategy}</p>
                </div>
              )}
            </div>
          )}
          {selectedItem && selectedItem.type === 'reply_draft' && (
            <div className="space-y-3">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs font-semibold mb-1">Original Message</p>
                <p className="text-sm">{selectedItem.incoming_message}</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs font-semibold mb-1">WhatsApp Reply</p>
                <p className="text-sm">{selectedItem.reply_whatsapp}</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs font-semibold mb-1">Email Reply</p>
                {selectedItem.reply_email_subject && <p className="text-xs text-muted-foreground mb-1">Subject: {selectedItem.reply_email_subject}</p>}
                <p className="text-sm whitespace-pre-line">{selectedItem.reply_email_body}</p>
              </div>
              {selectedItem.suggested_next_action && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs font-semibold mb-1">Next Action</p>
                  <p className="text-sm">{selectedItem.suggested_next_action}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Need to use a named import for Zap in the parent
const Zap = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/></svg>
);

export default NegotiationTab;
